import React from 'react';
import { SelectableDocument } from '../hooks/useRegenerateSelection';

interface DocumentSelectionSectionProps {
  title: string;
  description: string;
  documents: SelectableDocument[];
  onToggleDocument: (docId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  badgeColor: 'blue' | 'green';
  className?: string;
}

/**
 * Component for selecting documents in the regenerate modal
 * Displays documents with checkboxes, file info, and bulk selection controls
 */
export const DocumentSelectionSection: React.FC<DocumentSelectionSectionProps> = ({
  title,
  description,
  documents,
  onToggleDocument,
  onSelectAll,
  onDeselectAll,
  badgeColor,
  className = ''
}) => {
  const selectedCount = documents.filter(doc => doc.selected).length;
  const totalCount = documents.length;
  const allSelected = selectedCount === totalCount;
  const noneSelected = selectedCount === 0;

  const badgeClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700'
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      default:
        return '📄';
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (documents.length === 0) {
    return null;
  }

  return (
    <div className={`document-selection-section ${className}`}>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            {title}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            {description}
          </p>
        </div>
        
        {/* Selection Counter & Bulk Actions */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selectedCount} of {totalCount} selected
          </span>
          
          <div className="flex space-x-1">
            {!allSelected && (
              <button
                onClick={onSelectAll}
                className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
              >
                All
              </button>
            )}
            {!noneSelected && (
              <button
                onClick={onDeselectAll}
                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                None
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Document List */}
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {documents.map((document) => (
          <div
            key={document.id}
            className={`
              flex items-center p-3 border rounded-lg transition-colors cursor-pointer
              ${document.selected 
                ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
              }
              hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20
            `}
            onClick={() => onToggleDocument(document.id)}
          >
            {/* Checkbox */}
            <input
              type="checkbox"
              checked={document.selected}
              onChange={() => onToggleDocument(document.id)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-3"
              onClick={(e) => e.stopPropagation()}
            />

            {/* File Icon */}
            <span className="text-lg mr-3" aria-label="File type">
              {getFileIcon(document.name)}
            </span>

            {/* Document Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                  {document.name}
                </span>
                
                {/* Document Type Badge */}
                <span className={`
                  inline-block text-xs px-2 py-0.5 rounded-full border font-medium
                  ${badgeClasses[badgeColor]}
                `}>
                  {document.type === 'original' ? 'Original' : 'Added'}
                </span>
              </div>

              {/* Document Metadata */}
              <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                {document.policyType && (
                  <span>{document.policyType}</span>
                )}
                {document.size && (
                  <span>{formatFileSize(document.size)}</span>
                )}
                {document.type === 'additional' && document.status && (
                  <span className="text-green-600 dark:text-green-400">
                    ● {document.status}
                  </span>
                )}
              </div>
            </div>

            {/* Selection Indicator */}
            {document.selected && (
              <div className="flex-shrink-0 ml-2">
                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary Footer */}
      {documents.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {selectedCount === 0 && (
              <span className="text-orange-600 dark:text-orange-400 font-medium">
                ⚠️ At least one document must be selected
              </span>
            )}
            {selectedCount > 0 && (
              <span>
                Selected {selectedCount} document{selectedCount !== 1 ? 's' : ''} 
                {documents.some(d => d.size) && (
                  <span className="ml-1">
                    ({formatFileSize(
                      documents
                        .filter(d => d.selected && d.size)
                        .reduce((total, d) => total + (d.size || 0), 0)
                    )})
                  </span>
                )}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentSelectionSection;